import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Map } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import PostCard from '@/components/features/feed/PostCard'
import SearchBar from '@/components/ui/SearchBar'
import { CATS, STORIES, TRENDS, STRIPE, getBiz } from '@/data/mockData'
import { supabase } from '@/lib/supabase'
import { useMyInterests, useSavedPosts } from '@/hooks/useFeed'
import { useHorizontalScroll } from '@/hooks/useHorizontalScroll'
import { mapPostRow } from '@/lib/mapPost'
import type { PostData } from '@/data/mockData'

const TIMES = ['Ahora', 'Hoy', 'Esta noche', 'Mañana', 'Este fin de semana']
const C = {
  teal: '#0F5C5C',
  terra: '#D9785B',
  ink: '#202523',
  ink2: '#6D7471',
  border: '#E1E5E3',
  grayBg: '#F1F3F2',
}
const FH = "'Plus Jakarta Sans', sans-serif"
const FB = "'Inter', sans-serif"

export default function Home() {
  const navigate = useNavigate()
  const [timeFilter, setTimeFilter] = useState('Ahora')
  const [catFilter, setCatFilter] = useState<string | null>(null)
  const timesScrollRef = useHorizontalScroll<HTMLDivElement>()
  const storiesScrollRef = useHorizontalScroll<HTMLDivElement>()
  const catsScrollRef = useHorizontalScroll<HTMLDivElement>()

  // Cargar posts desde Supabase
  const { data: postsData, isLoading } = useQuery({
    queryKey: ['feed', timeFilter, catFilter],
    queryFn: async () => {
      if (!supabase) return null

      // Cargar negocios
      const { data: businesses } = await supabase
        .from('businesses')
        .select(
          'id, name, zone, verified, cover_colors, emoji, is_open, follower_count, category, latitude, longitude'
        )

      // Cargar actividades vigentes (no permanentes; las permanentes solo van al
      // perfil y a "Siguiendo"). Vigente = sin vencimiento o que aún no vence.
      let query = supabase
        .from('posts')
        .select('*')
        .eq('is_active', true)
        .eq('is_permanent', false)
        .or(`ends_at.is.null,ends_at.gt.${new Date().toISOString()}`)
        .order('created_at', { ascending: false })

      if (catFilter) query = query.eq('category', catFilter)
      if (timeFilter === 'Ahora') query = query.in('type', ['live', 'promo'])

      const { data: posts } = await query

      return { posts: posts ?? [], businesses: businesses ?? [] }
    },
    staleTime: 1000 * 60, // 1 min: evita re-fetch en cada navegación al inicio
  })

  const hr = new Date().getHours()
  const greeting = hr < 12 ? 'Buenos días' : hr < 19 ? 'Buenas tardes' : 'Buenas noches'

  const myInterests = useMyInterests()
  const { data: savedData } = useSavedPosts()
  const mySavedIds = new Set((savedData?.data ?? []).map((p) => p.id as string))

  // Convertir posts de Supabase al formato del PostCard
  const feed: PostData[] = postsData
    ? postsData.posts.map((p) =>
        mapPostRow(
          p,
          postsData.businesses.find((b) => b.id === p.business_id)
        )
      )
    : []

  // Posición real (lat/lng normalizada al recuadro) de cada negocio; si no
  // tiene coordenadas, cae al patrón decorativo anterior como respaldo.
  const geoBusinesses = (postsData?.businesses ?? []).filter(
    (b) => typeof b.latitude === 'number' && typeof b.longitude === 'number'
  )
  const lats = geoBusinesses.map((b) => b.latitude as number)
  const lngs = geoBusinesses.map((b) => b.longitude as number)
  const minLat = Math.min(...lats)
  const maxLat = Math.max(...lats)
  const minLng = Math.min(...lngs)
  const maxLng = Math.max(...lngs)
  const latRange = maxLat - minLat || 1
  const lngRange = maxLng - minLng || 1

  const dotPosition = (b: Record<string, unknown>, i: number) => {
    const lat = b.latitude as number | null
    const lng = b.longitude as number | null
    if (typeof lat === 'number' && typeof lng === 'number') {
      return {
        left: `${15 + ((lng - minLng) / lngRange) * 70}%`,
        top: `${12 + (1 - (lat - minLat) / latRange) * 58}%`,
      }
    }
    return { left: `${20 + ((i * 12) % 60)}%`, top: `${15 + ((i * 9) % 50)}%` }
  }

  // Mapa SVG con los negocios de Supabase (o mockData como fallback visual)
  const MiniMap = () => (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        background: 'linear-gradient(180deg,#dfe9e7,#eef2f0)',
        cursor: 'pointer',
      }}
      onClick={() => navigate('/map')}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: '36%',
          background: 'linear-gradient(180deg,#bcd6d6,#a9cccb)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: '42%',
          top: 0,
          bottom: 0,
          width: 3,
          background: 'rgba(21,61,61,.08)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: '52%',
          height: 3,
          background: 'rgba(21,61,61,.1)',
        }}
      />
      {(postsData?.businesses ?? []).slice(0, 8).map((b: Record<string, unknown>, i: number) => (
        <div
          key={b.id as string}
          style={{
            position: 'absolute',
            ...dotPosition(b, i),
            transform: 'translate(-50%,-100%)',
            animation: `pxFloat ${2.4 + i * 0.18}s ease-in-out infinite`,
          }}
        >
          <div
            style={{
              width: 22,
              height: 22,
              borderRadius: '50% 50% 50% 2px',
              background: C.teal,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 3px 8px rgba(0,0,0,.2)',
              border: '2px solid #fff',
              fontSize: 11,
            }}
          >
            {b.emoji as string}
          </div>
        </div>
      ))}
    </div>
  )

  // Rail derecho
  const Rail = () => (
    <aside
      style={{ position: 'sticky', top: 0, maxHeight: 'calc(100vh - 130px)', overflowY: 'auto' }}
    >
      <div
        style={{
          background: '#fff',
          border: `1px solid ${C.border}`,
          borderRadius: 16,
          padding: 16,
          marginBottom: 16,
        }}
      >
        <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 15, marginBottom: 12 }}>
          Mapa de actividades
        </div>
        <div
          style={{
            height: 190,
            borderRadius: 12,
            overflow: 'hidden',
            border: `1px solid ${C.border}`,
          }}
        >
          <MiniMap />
        </div>
        <button
          onClick={() => navigate('/map')}
          style={{
            width: '100%',
            marginTop: 10,
            padding: '9px',
            borderRadius: 10,
            border: `1px solid ${C.border}`,
            background: '#fff',
            fontFamily: FB,
            fontWeight: 700,
            fontSize: 13,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          <Map size={15} /> Abrir mapa completo
        </button>
      </div>

      <div
        style={{
          background: '#fff',
          border: `1px solid ${C.border}`,
          borderRadius: 16,
          padding: 16,
          marginBottom: 16,
        }}
      >
        <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 15, marginBottom: 12 }}>
          Tendencias
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
          {TRENDS.map((t) => (
            <button
              key={t.tag}
              onClick={() => navigate(`/search?q=${t.tag}`)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: C.grayBg,
                borderRadius: 99,
                padding: '7px 11px',
                cursor: 'pointer',
                fontFamily: FB,
                border: 'none',
                fontSize: 13,
              }}
            >
              <span style={{ fontWeight: 700, color: C.ink }}>#{t.tag}</span>
              <span style={{ color: C.ink2 }}>{t.n}</span>
            </button>
          ))}
        </div>
      </div>

      <div
        style={{
          background: '#fff',
          border: `1px solid ${C.border}`,
          borderRadius: 16,
          padding: 16,
        }}
      >
        <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 15, marginBottom: 12 }}>
          Categorías
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
          {CATS.map((c) => (
            <button
              key={c.k}
              onClick={() => setCatFilter(catFilter === c.k ? null : c.k)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: catFilter === c.k ? C.teal : C.grayBg,
                color: catFilter === c.k ? '#fff' : C.ink,
                borderRadius: 99,
                padding: '7px 11px',
                cursor: 'pointer',
                fontFamily: FB,
                fontWeight: 600,
                fontSize: 13,
                border: 'none',
              }}
            >
              {c.emoji} {c.label}
            </button>
          ))}
        </div>
      </div>
    </aside>
  )

  return (
    <div className="page">
      <SearchBar style={{ marginBottom: 20 }} />
      <div className="home-grid">
        {/* Columna principal */}
        <div style={{ minWidth: 0, overflow: 'hidden' }}>
          {/* Saludo + filtros */}
          <div style={{ marginBottom: 20 }}>
            <div
              style={{
                fontFamily: FH,
                fontWeight: 800,
                fontSize: 26,
                letterSpacing: '-0.01em',
                marginBottom: 4,
              }}
            >
              {greeting} 👋
            </div>
            <div style={{ fontSize: 15, color: C.ink2, marginBottom: 16 }}>
              ¿Qué parche buscas hoy en Pereira, Risaralda?
            </div>
            <div
              ref={timesScrollRef}
              style={{
                display: 'flex',
                gap: 8,
                overflowX: 'auto',
                scrollbarWidth: 'none',
                paddingBottom: 4,
                width: '100%',
              }}
            >
              {TIMES.map((t) => (
                <button
                  key={t}
                  onClick={() => setTimeFilter(t)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    whiteSpace: 'nowrap',
                    padding: '8px 14px',
                    borderRadius: 99,
                    flexShrink: 0,
                    border: `1px solid ${timeFilter === t ? C.teal : C.border}`,
                    background: timeFilter === t ? C.teal : '#fff',
                    color: timeFilter === t ? '#fff' : C.ink,
                    fontFamily: FB,
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  {t === 'Ahora' && (
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: 99,
                        background: timeFilter === 'Ahora' ? '#fff' : '#D85F5F',
                        animation: 'pxPulse 1.4s infinite',
                      }}
                    />
                  )}
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Stories */}
          <div style={{ marginBottom: 20, width: '100%', overflow: 'hidden' }}>
            <div
              ref={storiesScrollRef}
              style={{
                display: 'flex',
                gap: 8,
                overflowX: 'auto',
                scrollbarWidth: 'none',
                width: '100%',
              }}
            >
              {STORIES.map((s) => {
                const b = getBiz(s.biz)
                if (!b) return null
                return (
                  <button
                    key={s.id}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 6,
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      width: 78,
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        padding: 2.5,
                        borderRadius: 99,
                        background: `linear-gradient(135deg,${s.color},#D3A34A)`,
                      }}
                    >
                      <div
                        style={{
                          width: 60,
                          height: 60,
                          borderRadius: 99,
                          background: STRIPE(b.cover[0], b.cover[1]),
                          border: '2.5px solid #fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 24,
                        }}
                      >
                        {b.emoji}
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: C.ink,
                        textAlign: 'center',
                        lineHeight: 1.1,
                      }}
                    >
                      {s.cat}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Filtros categoría */}
          <div style={{ marginBottom: 18 }}>
            <div
              ref={catsScrollRef}
              style={{
                display: 'flex',
                gap: 8,
                overflowX: 'auto',
                scrollbarWidth: 'none',
                width: '100%',
              }}
            >
              {CATS.map((c) => (
                <button
                  key={c.k}
                  onClick={() => setCatFilter(catFilter === c.k ? null : c.k)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    whiteSpace: 'nowrap',
                    padding: '8px 14px',
                    borderRadius: 99,
                    flexShrink: 0,
                    border: `1px solid ${catFilter === c.k ? C.teal : C.border}`,
                    background: catFilter === c.k ? C.teal : '#fff',
                    color: catFilter === c.k ? '#fff' : C.ink,
                    fontFamily: FB,
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  {c.emoji} {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Feed header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 14,
            }}
          >
            <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 19 }}>
              {catFilter
                ? `Resultados · ${CATS.find((c) => c.k === catFilter)?.label}`
                : 'En tu feed'}
            </div>
            {catFilter && (
              <button
                onClick={() => setCatFilter(null)}
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: C.teal,
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Ver todo
              </button>
            )}
          </div>

          {/* Posts */}
          {isLoading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    height: 320,
                    borderRadius: 18,
                    background: '#fff',
                    border: `1px solid ${C.border}`,
                    animation: 'pxPulse 1.5s infinite',
                  }}
                />
              ))}
            </div>
          ) : feed.length > 0 ? (
            <div className="feed-grid-3">
              {feed.map((p) => (
                <PostCard
                  key={p.id}
                  post={p}
                  initialInterested={myInterests.has(p.id)}
                  initialSaved={mySavedIds.has(p.id)}
                />
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '70px 24px' }}>
              <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 19, marginBottom: 8 }}>
                Sin resultados
              </div>
              <div style={{ fontSize: 14, color: C.ink2, marginBottom: 20 }}>
                No hay publicaciones para este filtro.
              </div>
              <button
                onClick={() => {
                  setCatFilter(null)
                  setTimeFilter('Ahora')
                }}
                style={{
                  background: C.teal,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 11,
                  padding: '11px 22px',
                  fontFamily: FB,
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                Quitar filtros
              </button>
            </div>
          )}
        </div>

        {/* Rail */}
        <Rail />
      </div>
    </div>
  )
}
